import SquadDivisionStrength from "../components/SquadDivisionStrength.js";
import ButtonSelect from "shared/components/buttons/ButtonSelect.js";
import { requestWithCallbacks as request } from "shared/utils/usePendingAction.js";
import React, { useEffect, useRef, useState } from "react";
import BoxCard from "shared/components/box/BoxCard.js";
import BoxLogin from "shared/components/box/BoxLogin.js";
import BoxMessage from "shared/components/box/BoxMessage.js";
import ButtonLogin from "shared/components/buttons/ButtonLogin.js";
import ButtonPlayerView from "shared/components/buttons/ButtonPlayerView.js";
import ControllerTeams, { ControllerTeamsAddButton } from "shared/components/controllers/ControllerTeams";
import ItemRowPlayerAssist from "shared/components/items/ItemRowPlayerAssist.js";
import ItemRowWarning from "shared/components/items/ItemRowWarning.js";
import ItemRowTeamNote from "shared/components/items/ItemRowTeamNote.js";
import LoadingSquare from "shared/components/loading/LoadingSquare.js";
import PopupAddPlayers from "shared/components/popups/PopupAddPlayers.js";
import PopupSelectClub from "shared/components/popups/PopupSelectClub.js";
import PopupConfirmation from "shared/components/popups/PopupConfirmation.js";
import PopupAddTeamNote from "shared/components/popups/PopupAddTeamNote.js";
import MiscFootballFieldForSquad from "shared/components/misc/MiscFootballFieldForSquad.js";
import { getCalculatedOverall } from "shared/utils/player.js";
import { getSquadWarnings } from "shared/utils/squad.js";
import { getOverallTag } from "shared/utils/player.js";
import Count from "shared/components/counts/Count.js";
import {
  addTeamMembers,
  deleteTeamMember,
  getTeams,
  updateTeam,
  updateTeamMember,
  updateTeamMembers,
  moveTeam,
  getTeamNotesOfUser,
} from "shared/services/api-assistant.js";
import { getPlayersFromClub } from "shared/services/api-mfl.js";
import { formations } from "shared/utils/formation.js";
import { calculateTeamOverall } from "shared/utils/squad.js";
import "./../bars/MenuTop.css";

const animateSquadPanel = (panel) => {
  if (!panel || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  panel.getAnimations().forEach((animation) => animation.cancel());
  panel.animate(
    [
      { opacity: 0, transform: "translateX(24px)" },
      { opacity: 1, transform: "translateX(0)" },
    ],
    { duration: 300, easing: "ease-out" },
  );
};

const PageSquadBuilder = (props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [actionError, setActionError] = useState(null);
  const actionInProgress = useRef(false);

  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [teams, setTeams] = useState(null);
  const [notes, setNotes] = useState(null);

  const [view, setView] = useState("players");
  const [playerView, setPlayerView] = useState(null);
  const [showOnfieldPlayers, setShowOnfieldPlayers] = useState(false);
  const [showWarnings, setShowWarnings] = useState(true);
  const [showNotes, setShowNotes] = useState(true);
  const squadPanelRef = useRef(null);
  const squadViewRef = useRef(null);

  useEffect(() => {
    animateSquadPanel(squadPanelRef.current);
  }, [selectedTeamId]);

  useEffect(() => {
    animateSquadPanel(squadViewRef.current);
  }, [view]);

  const refreshTeams = async () => {
    const response = await request(getTeams);
    const nextTeams = response.data.getTeams || [];
    setTeams(nextTeams);
    setSelectedTeamId((current) =>
      nextTeams.some((team) => team.id === current) ? current : nextTeams[0]?.id || null,
    );
  };

  const runAction = async (message, action, { showLoading = true } = {}) => {
    if (actionInProgress.current) return;
    actionInProgress.current = true;
    setActionError(null);
    if (showLoading) {
      setLoadingMessage(message);
      setIsLoading(true);
    }
    try {
      await action();
    } catch (error) {
      console.error(message, error);
      setActionError("Could not finish the action. Refresh the squad to check its latest state before trying again.");
    } finally {
      actionInProgress.current = false;
      if (showLoading) setIsLoading(false);
    }
  };

  const fetchTeams = () => {
    if (props.assistantUser)
      return runAction("Loading squads…", async () => {
        const results = await Promise.allSettled([refreshTeams(), fetchTeamNotes()]);
        const failure = results.find((result) => result.status === "rejected");
        if (failure) throw failure.reason;
      }, { showLoading: false });
  };

  const fetchTeamNotes = async () => {
    if (!props.assistantUser) return;
    const response = await request(getTeamNotesOfUser);
    setNotes(response.data.getTeamNotesOfUser || []);
  };

  const mutateTeam = (message, api, params) =>
    runAction(message, async () => {
      await request(api, { params });
      await refreshTeams();
    });

  const addTeamMembersInGroup = (teamId, playerIds) =>
    mutateTeam("Adding players…", addTeamMembers, { teamId, playerIds });

  const clearFormationAndPositions = (teamId) => {
    const members = getSelectedTeam().teamMembers.map((member) => ({ id: member.id, position: null }));
    return runAction("Clearing formation…", async () => {
      await request(updateTeam, { params: { id: teamId, formation: null } });
      await request(updateTeamMembers, { params: { members, teamId } });
      await refreshTeams();
    });
  };

  const deleteTeamMemberInGroup = (id) => mutateTeam("Removing player…", deleteTeamMember, { teamMemberId: id });

  const saveTeam = (teamId, data) => mutateTeam("Saving formation…", updateTeam, { id: teamId, ...data });

  const changeTeamPosition = (id, position) => mutateTeam("Reordering teams…", moveTeam, { id, position });

  const saveTeamMember = (id, position) => mutateTeam("Updating player position…", updateTeamMember, { id, position });

  const saveTeamMembers = (teamId, formation, members) =>
    runAction("Applying lineup…", async () => {
      await request(updateTeamMembers, { params: { members, teamId } });
      await request(updateTeam, { params: { id: teamId, formation } });
      await refreshTeams();
    });

  const emptyTeamMembers = (teamId) => mutateTeam("Clearing squad…", updateTeamMembers, { members: [], teamId });

  const getSelectedTeam = () => {
    if (selectedTeamId && teams) {
      return teams.filter((t) => t.id === selectedTeamId).pop();
    }
    return null;
  };

  const getSelectedTeamMembers = () =>
    getSelectedTeam()?.teamMembers?.filter((member) => member?.player) || [];

  const importFromClub = (clubId) => {
    const teamId = getSelectedTeam().id;
    return runAction("Importing club players…", async () => {
      const players = await request(getPlayersFromClub, { clubId });
      await request(addTeamMembers, { params: { teamId, playerIds: players.map((player) => player.id) } });
      await refreshTeams();
    });
  };

  const getTopPlayerOveralls = (limit) => {
    const team = getSelectedTeam();

    if (!team?.teamMembers || team.teamMembers.length === 0) {
      return [];
    }

    return team.teamMembers
      .filter((tm) => tm?.player)
      .map((tm) => tm.player.overall)
      .sort((a, b) => b - a)
      .slice(0, limit);
  };

  const getIG = () => {
    const team = getSelectedTeam();

    if (!team?.teamMembers || team.teamMembers.length === 0) {
      return "--";
    }

    const formationOveralls = Object.keys(formations)
      .map((formation) => {
        return calculateTeamOverall(formation, team.teamMembers.filter((member) => member?.player))[0];
      })
      .filter((overall) => overall !== null && overall !== undefined);

    if (formationOveralls.length === 0) {
      return "--";
    }

    return Math.max(...formationOveralls);
  };

  const getB11A = () => {
    const players = getTopPlayerOveralls(11);

    if (players.length === 0) return "--";

    return players.reduce((sum, overall) => sum + overall, 0) / players.length;
  };

  const getB16A = () => {
    const players = getTopPlayerOveralls(16);

    if (players.length === 0) return "--";

    return players.reduce((sum, overall) => sum + overall, 0) / players.length;
  };

  const getB11 = () => {
    const players = getTopPlayerOveralls(11);
    if (players.length === 0) return "--";
    return players.reduce((sum, overall) => sum + overall, 0);
  };

  const getB16 = () => {
    const players = getTopPlayerOveralls(16);
    if (players.length === 0) return "--";
    return players.reduce((sum, overall) => sum + overall, 0);
  };

  const getMMR = () => {
    const b11a = getB11A();
    const b16a = getB16A();
    const totalIG = getIG();
    const ig = totalIG === "--" ? null : totalIG / 11;

    if (b11a === "--" || b16a === "--" || ig === null) {
      return "--";
    }

    return Math.round(0.3333 * (b11a * 100 + b16a * 100 + ig * 100)) / 100;
  };

  useEffect(() => {
    fetchTeams();
  }, []);

  useEffect(() => {
    if (props.assistantUser) {
      fetchTeams();
    }
  }, [props.assistantUser]);

  if (!props.assistantUser) {
    return (
      <div className="d-flex h-100 justify-content-center align-items-center">
        <ButtonLogin
          flowUser={props.flowUser}
          assistantUser={props.assistantUser}
          logout={props.logout}
          content={<BoxLogin assistantUser={props.assistantUser} />}
        />
      </div>
    );
  }

  return (
    <div id="PageSquadBuilder" className="h-100 w-100">
      <div className="d-flex flex-column h-100 w-100">
        <nav className="MenuTop navbar">
          <h3 className="my-2 ps-5">
            <i className="bi bi-person-rolodex me-2"></i> Squad builder
          </h3>
          {isLoading && (
            <div className="d-flex align-items-center gap-2 px-3 primary-color" role="status" aria-live="polite">
              <span className="spinner-border spinner-border-sm" aria-hidden="true" />
              {loadingMessage}
            </div>
          )}
        </nav>
        {actionError && (
          <div className="alert alert-danger mx-3 my-2 d-flex align-items-center justify-content-between" role="alert">
            <span>{actionError}</span>
            <button className="btn btn-sm btn-outline-light ms-2" disabled={isLoading} onClick={fetchTeams}>
              Refresh
            </button>
          </div>
        )}

        <div
          className="position-relative d-flex flex-fill w-100"
          aria-busy={isLoading}
          inert={isLoading ? "" : undefined}
          style={{ minHeight: 0, pointerEvents: isLoading ? "none" : undefined }}
        >
          {isLoading && (
            <div
              className="position-absolute top-0 start-0 w-100 h-100 d-flex"
              style={{ zIndex: 20 }}
              aria-hidden="true"
            >
              <LoadingSquare height="100%" />
            </div>
          )}
          <div
            className="d-flex container-fluid px-1 px-md-4 py-4 flex-grow-1 h-md-100 w-100"
            style={{ opacity: isLoading ? 0.55 : 1 }}
          >
            <div className="d-flex flex-column flex-fill flex-md-row h-md-100 w-100">
              <div className="flex-shrink-0 flex-md-basis-200" style={{ minWidth: 0 }}>
                <BoxCard
                  className="fit-content"
                  title={"Teams"}
                  actions={<ControllerTeamsAddButton teams={teams} user={props.assistantUser} fetchTeams={fetchTeams} />}
                  content={
                    <ControllerTeams
                      hideAddButton
                      onSelect={(c) => {
                        setSelectedTeamId(c);
                      }}
                      teams={teams}
                      selectedTeamId={selectedTeamId}
                      fetchTeams={fetchTeams}
                      onMove={changeTeamPosition}
                      user={props.assistantUser}
                      notes={notes}
                    />
                  }
                />
              </div>

              {getSelectedTeam() && (
                <div ref={squadPanelRef} className="d-flex flex-fill flex-column h-md-100">
                  <BoxCard
                    className="pb-2 flex-shrink-0"
                    content={
                      !teams ? (
                        <div className="w-100" style={{ height: "40px" }}>
                          <LoadingSquare />
                        </div>
                      ) : (
                        <div className="d-flex flex-column flex-sm-row flex-fill">
                          <div className="d-flex flex-grow-1 align-items-center">
                            <h4 className="mb-0">{getSelectedTeam().name}</h4>
                          </div>
                          <div className="d-flex justify-content-end align-items-center">
                            <ButtonSelect
                              ariaLabel="Squad builder view"
                              selected={view}
                              onChange={setView}
                              buttons={{ players: "Players and notes", strength: "Division strength" }}
                            />
                          </div>
                        </div>
                      )
                    }
                  />

                  <div className="d-flex flex-column flex-md-row flex-fill">
                    <div className="d-flex flex-column flex-lg-basis-500 flex-md-basis-400">
                      <BoxCard
                        className="p-0"
                        noPadding={true}
                        content={
                          <div className="d-flex flex-column w-100">
                            <div className="d-flex flex-row w-100">
                              <div className="d-flex flex-fill mt-1 mx-2">
                                <select
                                  className={
                                    "form-select mb-1 w-100 " +
                                    ((getSelectedTeam()?.formation || "") === "" ? "highlighted" : "")
                                  }
                                  value={getSelectedTeam()?.formation || ""}
                                  onChange={(v) => saveTeam(getSelectedTeam().id, { formation: v.target.value })}
                                  placeholder={"Formation"}
                                >
                                  <option value={""} key={""}>
                                    No formation
                                  </option>
                                  {Object.keys(formations).map((f) => (
                                    <option value={f} key={f}>
                                      {f}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              {getSelectedTeam().formation && (
                                <div className="pt-1 mt-1 me-1 pe-1">
                                  <button
                                    className="btn btn-sm btn-info text-white mb-1"
                                    onClick={() => clearFormationAndPositions(getSelectedTeam().id)}
                                  >
                                    <i className="bi bi-x-lg"></i>
                                  </button>
                                </div>
                              )}
                            </div>

                            <div className="position-relative d-flex flex-fill ratio ratio-1x1 flex-lg-basis-500 flex-md-basis-400">
                              {getSelectedTeam()?.formation ? (
                                <MiscFootballFieldForSquad
                                  team={{ ...getSelectedTeam(), teamMembers: getSelectedTeamMembers() }}
                                  formation={getSelectedTeam().formation}
                                  saveTeamMember={saveTeamMember}
                                />
                              ) : (
                                <div className={"d-flex flex-column flex-fill"}>
                                  <div
                                    className="football-field position-absolute w-100 h-100 ratio ratio-1x1"
                                    style={{ zIndex: 1 }}
                                  >
                                    <div className="football-field-lines">
                                      <div className="halfway-line" />
                                      <div className="center-circle" />
                                      <div className="goal-box" />
                                    </div>
                                  </div>
                                  <div
                                    className={"d-flex flex-column flex-fill w-100 h-100 overflow-auto"}
                                    style={{ zIndex: 2, opacity: 0.9 }}
                                  >
                                    <div className={"Item no-hover d-flex flex-row py-1 px-2"}>
                                      <div className={"d-flex flex-grow-1"}>Formation</div>
                                      <div className={"d-flex justify-content-center flex-basis-80"}>OVR</div>
                                      <div className={"d-flex flex-basis-80"}></div>
                                    </div>
                                    {Object.keys(formations)
                                      .map((f) => {
                                        const overall = calculateTeamOverall(f, getSelectedTeamMembers())[0];
                                        return { formation: f, overall };
                                      })
                                      .sort((a, b) => b.overall - a.overall) // Sort by overall in descending order
                                      .map(({ formation, overall }) => (
                                        <div className={"Item no-hover d-flex flex-row px-2"} key={formation}>
                                          <div className={"d-flex flex-grow-1"}>{formation}</div>
                                          <div className={"d-flex justify-content-center flex-basis-80"}>{overall}</div>
                                          <div className={"d-flex justify-content-end flex-basis-80"}>
                                            <button
                                              className="btn btn-xs btn-info text-white"
                                              onClick={() =>
                                                saveTeamMembers(
                                                  getSelectedTeam().id,
                                                  formation,
                                                  calculateTeamOverall(formation, getSelectedTeamMembers())[1],
                                                )
                                              }
                                            >
                                              Select
                                            </button>
                                          </div>
                                        </div>
                                      ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        }
                      />
                      <div className="d-flex flex-column flex-sm-row">
                        <BoxCard
                          className="mb-2 flex-sm-basis-140"
                          content={
                            <div className="d-flex flex-fill mt-2 justify-content-center">
                              <div className="d-flex flex-fill flex-column align-items-center justify-content-center">
                                <Count
                                  label="MMR"
                                  count={getMMR() !== "--" ? getOverallTag(getMMR().toFixed(1)) : "--"}
                                />
                              </div>
                            </div>
                          }
                        />
                        <BoxCard
                          className="flex-fill"
                          content={
                            <div className="row w-100 my-2 d-flex justify-content-center align-items-center">
                              <div className="col-4 d-flex justify-content-center align-items-center mt-2">
                                <Count
                                  label="Starter"
                                  count={
                                    getSelectedTeam().teamMembers &&
                                    getSelectedTeam().formation &&
                                    getSelectedTeam()
                                      .teamMembers.filter((t) => t?.player)
                                      .filter((tm) => tm.position).length > 0
                                      ? getSelectedTeam()
                                          .teamMembers.filter((t) => t?.player)
                                          .filter((tm) => tm.position)
                                          .map((tm) =>
                                            getCalculatedOverall(
                                              tm.player,
                                              formations[getSelectedTeam().formation][tm.position].position,
                                            ),
                                          )
                                          .reduce((acc, cur) => acc + cur, 0)
                                      : "--"
                                  }
                                />
                              </div>

                              <div className="col-4 d-flex justify-content-center align-items-center mt-2">
                                <Count label="B11" count={getB11()} />
                              </div>

                              <div className="col-4 d-flex justify-content-center align-items-center mt-2">
                                <Count label="B16" count={getB16()} />
                              </div>
                              <div className="col-4 d-flex justify-content-center align-items-center mt-2">
                                <Count
                                  label="StarterA"
                                  count={
                                    getSelectedTeam().teamMembers &&
                                    getSelectedTeam().formation &&
                                    getSelectedTeam()
                                      .teamMembers.filter((t) => t?.player)
                                      .filter((tm) => tm.position).length > 0
                                      ? (
                                          getSelectedTeam()
                                            .teamMembers.filter((t) => t?.player)
                                            .filter((tm) => tm.position)
                                            .map((tm) =>
                                              getCalculatedOverall(
                                                tm.player,
                                                formations[getSelectedTeam().formation][tm.position].position,
                                              ),
                                            )
                                            .reduce((acc, cur) => acc + cur, 0) /
                                          getSelectedTeam()
                                            .teamMembers.filter((t) => t?.player)
                                            .filter((tm) => tm.position).length
                                        ).toFixed(1)
                                      : "--"
                                  }
                                />
                              </div>

                              <div className="col-4 d-flex justify-content-center align-items-center mt-2">
                                <Count
                                  label="B11A"
                                  count={
                                    getSelectedTeam().teamMembers &&
                                    getSelectedTeam().teamMembers.length > 0 &&
                                    getB11A() !== "--"
                                      ? getB11A().toFixed(1)
                                      : "--"
                                  }
                                />
                              </div>

                              <div className="col-4 d-flex justify-content-center align-items-center mt-2">
                                <Count
                                  label="B16A"
                                  count={
                                    getSelectedTeam().teamMembers &&
                                    getSelectedTeam().teamMembers.length > 0 &&
                                    getB16A() !== "--"
                                      ? getB16A().toFixed(1)
                                      : "--"
                                  }
                                />
                              </div>
                            </div>
                          }
                        />
                      </div>
                    </div>

                    <div ref={squadViewRef} className="d-flex flex-column flex-fill" style={{ minWidth: 0 }}>
                      <SquadDivisionStrength
                        active={view === "strength"}
                        team={getSelectedTeam()}
                        metrics={
                          view === "strength"
                            ? {
                                playerCount: getSelectedTeamMembers().length,
                                b11: getB11(),
                                b11a: getB11A(),
                                b16a: getB16A(),
                                ig11: getIG() === "--" ? null : getIG() / 11,
                                mmr: getMMR(),
                              }
                            : null
                        }
                      />
                      {view === "players" && (
                        <>
                          <BoxCard
                            className="d-flex flex-fill pb-2"
                            contentClassName={"h-md-100"}
                            title="Players"
                            actions={
                              <div className="d-flex flex-wrap justify-content-end align-items-center gap-1 mb-2">
                                {getSelectedTeam().teamMembers?.length > 0 && (
                                  <PopupConfirmation
                                    trigger={
                                      <button className="btn btn-sm btn-link">
                                        <i className="bi bi-person-x-fill"></i> Clear
                                      </button>
                                    }
                                    title={"Are you sure?"}
                                    content={"Are you sure you want to remove all the players from the squad?"}
                                    onConfirm={() => emptyTeamMembers(getSelectedTeam().id)}
                                  />
                                )}

                                <PopupSelectClub
                                  trigger={
                                    <button className="btn btn-sm btn-link">
                                      <i className="bi bi-download"></i> Import
                                    </button>
                                  }
                                  onConfirm={(club) => importFromClub(club.id)}
                                />

                                <PopupAddPlayers
                                  trigger={
                                    <button className="btn btn-sm btn-info text-white">
                                      <i className="bi bi-person-plus-fill me-1"></i> Add
                                    </button>
                                  }
                                  onConfirm={(players) =>
                                    addTeamMembersInGroup(
                                      getSelectedTeam().id,
                                      players.map((p) => p.id),
                                    )
                                  }
                                  userId={props.assistantUser.id}
                                />
                              </div>
                            }
                            content={
                              <div className="d-flex flex-fill flex-column flex-md-row h-md-100">
                                <div className="d-flex flex-grow-1">
                                  <div className="d-flex flex-fill flex-column">
                                    {getSelectedTeam().teamMembers?.length > 0 && (
                                      <div className="d-flex flex-wrap justify-content-end align-items-center gap-2 mb-2">
                                        <small>
                                          Show on-field players
                                          <input
                                            type="checkbox"
                                            className="m-1"
                                            checked={showOnfieldPlayers}
                                            onChange={() => setShowOnfieldPlayers(!showOnfieldPlayers)}
                                          />
                                        </small>

                                        <ButtonPlayerView
                                          selectedView={playerView}
                                          onChange={(v) => setPlayerView(v)}
                                          displayOwner={true}
                                          displayTraits={true}
                                        />
                                      </div>
                                    )}
                                    {getSelectedTeam().teamMembers && getSelectedTeam().teamMembers.length === 0 && (
                                      <BoxMessage className={"py-4 py-md-0"} content={"No player in the group"} />
                                    )}

                                    {getSelectedTeam().teamMembers && getSelectedTeam().teamMembers.length > 0 && (
                                      <div className="d-flex flex-column flex-grow-1 h-md-100">
                                        <div className="d-flex flex-column overflow-auto h-100">
                                          {getSelectedTeam()
                                            .teamMembers.filter((p) => p && (!p.position || showOnfieldPlayers))
                                            .filter((t) => t?.player)
                                            .sort((a, b) => b.player.overall - a.player.overall)
                                            .map((p) => (
                                              <div className="ItemRowMatch">
                                                <ItemRowPlayerAssist
                                                  p={p.player}
                                                  display={playerView}
                                                  onDelete={() => deleteTeamMemberInGroup(p.id)}
                                                />
                                              </div>
                                            ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            }
                          />

                          <BoxCard
                            className="d-flex flex-fill pb-2 max-height-md-300"
                            title="Notes"
                            contentClassName={"h-md-100"}
                            actions={
                              <div className="d-flex flex-wrap justify-content-end align-items-center gap-2 mb-2">
                                <div className="d-flex flex-wrap justify-content-end align-items-center">
                                  <small>
                                    Show warnings
                                    <input
                                      type="checkbox"
                                      className="m-1 me-2"
                                      checked={showWarnings}
                                      onChange={() => setShowWarnings(!showWarnings)}
                                    />
                                  </small>
                                  <small>
                                    Show notes
                                    <input
                                      type="checkbox"
                                      className="m-1 me-2"
                                      checked={showNotes}
                                      onChange={() => setShowNotes(!showNotes)}
                                    />
                                  </small>
                                </div>
                                <PopupAddTeamNote
                                  trigger={
                                    <button className="btn btn-sm btn-info text-white mb-1">
                                      <i className="bi bi-plus-circle-fill"></i> Add note
                                    </button>
                                  }
                                  onConfirm={fetchTeamNotes}
                                  teamId={selectedTeamId}
                                />
                              </div>
                            }
                            content={
                              <div className="d-flex flex-fill flex-column flex-md-row h-md-100">
                                <div className="d-flex flex-grow-1">
                                  <div className="d-flex flex-fill flex-column">
                                    <div className="d-flex flex-column flex-grow-1 h-md-100">
                                      <div className="d-flex flex-column flex-grow-1 overflow-auto">
                                        {showNotes &&
                                          notes !== null &&
                                          notes
                                            .filter((t) => t.team.id === selectedTeamId)
                                            .map((w) => <ItemRowTeamNote c={w} afterDelete={() => fetchTeamNotes()} />)}
                                        {showWarnings &&
                                          getSquadWarnings(getSelectedTeam(), teams).map(
                                            (w) => <ItemRowWarning key={w} c={w} />,
                                          )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            }
                          />
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PageSquadBuilder;
